import { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import axios from "axios";
import FavoriteButton from "src/pages/DashboardPage/acadia/components/FavoriteButton";
import DialogComponent from "src/components/DialogComponent";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import InputFilterSet from "src/components/InputFilterSet";
import TGButton from "src/components/ui/TGButton";
import SaveIcon from "src/components/ui-icons/SaveIcon";
import { useAppSelector } from "src/hooks/redux";
import { SelectedCurrentUser } from "src/modules/users/types";
import { selectCurrentUser } from "src/modules/users/selector";
import { getItemsRequest } from "src/modules/spItems/api";
import Loading from "src/components/Loading";
import { MTFavoriteFiltersListName } from "src/api/config/myTasksLists.config";
import CustomRadio from "src/pages/DashboardPage/acadia/components/Tests/components/Ui/CustomRadio";
import { listMap } from "src/api/config/systemSwitch";
import { strapiUrl } from "src/modules/users/constants";
import { isPWA } from "src/utils/mobile";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

export const FavoriteButtonFilters = ({
  filters,
  displayFilters,
  reRenderFilterSettings,
  setReRenderFilterSettings,
  filtersParams,
  type,
  extraCompareData,
}: {
  filters: string;
  displayFilters: string;
  reRenderFilterSettings: boolean;
  setReRenderFilterSettings: any;
  filtersParams: any;
  type: string;
  extraCompareData?: any;
}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isFavorite, setIsFavorite] = useState<{
    state: boolean;
    id: number | null;
  }>({ state: false, id: null });
  const currentUser: SelectedCurrentUser = useAppSelector(selectCurrentUser);
  const [loading, setLoading] = useState(false);
  const [filterTitle, setFilterTitle] = useState("");
  const [defaultFilter, setDefaultFilter] = useState(false);
  const [sendFilters, setSendFilters] = useState<any>({});
  const [sendDisplayFilters, setSendDisplayFilters] = useState<any>({});
  const [sendFiltersParams, setSendFiltersParams] = useState<any>({});

  const parseJson = (value: any) => {
    if (!value) return null;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const compactParams = (value: any): any => {
    if (value == null) return undefined;
    if (Array.isArray(value)) {
      const next = value
        .map((v) => compactParams(v))
        .filter((v) => v !== undefined);
      return next.length > 0 ? next : undefined;
    }
    if (typeof value === "object") {
      const next: any = {};
      Object.keys(value).forEach((k) => {
        const v = (value as any)[k];
        if (v === null || v === undefined || v === "") return;
        const compacted = compactParams(v);
        if (compacted === undefined) return;
        if (typeof compacted === "object" && !Array.isArray(compacted) && Object.keys(compacted).length === 0) return;
        next[k] = compacted;
      });
      if (
        ("startDate" in next || "endDate" in next) &&
        (next.startDate == null || next.startDate === "") &&
        (next.endDate == null || next.endDate === "")
      ) {
        return undefined;
      }
      return Object.keys(next).length > 0 ? next : undefined;
    }
    return value;
  };


  const listStrapiApi = useMemo(
    () => listMap[MTFavoriteFiltersListName].replace(":folder", ""),
    []
  );

  const getDocumentIdById = async (id: number) => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await axios.get(
      `${strapiUrl}${listStrapiApi}?filters[id][$eq]=${id}`,
      { headers, withCredentials: true }
    );
    const documentId = res?.data?.data?.[0]?.documentId;
    if (!documentId) {
      throw new Error("Не удалось получить documentId для удаления/обновления");
    }
    return documentId as number;
  };

  const addFavoriteFilter = async (data: Record<string, any>) => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await axios.post(
      `${strapiUrl}${listStrapiApi}`,
      { data },
      { headers, withCredentials: true }
    );
    return res?.data?.data?.id as number;
  };

  const updateFavoriteFilter = async (id: number, data: Record<string, any>) => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const documentId = await getDocumentIdById(id);
    await axios.put(
      `${strapiUrl}${listStrapiApi}/${documentId}`,
      { data },
      { headers, withCredentials: true }
    );
  };

  const deleteFavoriteFilter = async (id: number) => {
    let token: string | null = null;

    if (!isPWA()) {
      const result = await SecureStoragePlugin.get({ key: 'accessToken' });
      token = result.value;
    }

    const headers = {
      Accept: "application/json;odata=nometadata",
      "Content-Type": "application/json;odata=nometadata",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const documentId = await getDocumentIdById(id);
    await axios.delete(`${strapiUrl}${listStrapiApi}/${documentId}`, {
      headers,
      withCredentials: true,
    });
  };

  useEffect(() => {
    let canceled = false;

    const run = async () => {
      setLoading(true);
      try {
        if (Object.keys(currentUser).length > 0 && currentUser.id > 0) {
          const results = await getItemsRequest({
            listName: MTFavoriteFiltersListName,
            filters: { Author: currentUser.id, Type: type },
          });
          const rawCurrent = parseJson(filtersParams) ?? {};
          const mergedCurrent: any = { ...rawCurrent };
          if (extraCompareData && typeof extraCompareData === "object") {
            Object.keys(extraCompareData).forEach((k) => {
              if (extraCompareData[k] !== undefined && extraCompareData[k] !== null) {
                mergedCurrent[k] = extraCompareData[k];
              }
            });
          }
          const currentParamsObj = compactParams(mergedCurrent) ?? {};
          const favoriteFilters = [
            ...results.items.filter((item: any) => {
              const savedObj = compactParams(parseJson(item.MTFullFilters) ?? {}) ?? {};
              return _.isEqual(savedObj, currentParamsObj);
            }),
          ];
          if (canceled) return;
          if (favoriteFilters.length > 0) {
            setIsFavorite({
              state: true,
              id: favoriteFilters[0]["ID"],
            });
          } else {
            setIsFavorite({ state: false, id: null });
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (!canceled) setLoading(false);
      }
    };

    run();

    return () => {
      canceled = true;
    };
  }, [reRenderFilterSettings, filtersParams, currentUser, type, extraCompareData]);

  useEffect(() => {
    if (filters) {
      const tempFilters = { ...JSON.parse(filters) };
      setSendFilters({ ...tempFilters });
    }
  }, [filters]);

  useEffect(() => {
    if (displayFilters) {
      const tempFilters = { ...JSON.parse(displayFilters) };
      setSendDisplayFilters({ ...tempFilters });
    }
  }, [displayFilters]);

  useEffect(() => {
    if (filtersParams) {
      const tempFilters = { ...JSON.parse(filtersParams) };
      tempFilters.hasOwnProperty("__mtFavoriteBump") && delete tempFilters["__mtFavoriteBump"];
      setSendFiltersParams({ ...(compactParams(tempFilters) ?? {}) });
    }
  }, [filtersParams]);

  const toggleFavorite = async () => {
    setLoading(true);
    closeDialog();

    try {
      if (!isFavorite.id) {
        const results = await getItemsRequest({
          listName: MTFavoriteFiltersListName,
          filters: { Author: currentUser.id, Type: type },
        });
        const filtersUser = [...results.items];

        if (filtersUser.length > 0 && defaultFilter) {
          await Promise.all(
            filtersUser.map((filter: any) =>
              updateFavoriteFilter(filter.ID, { DefaultFilter: false })
            )
          );
        }

        await addFavoriteFilter({
          MTFilters: JSON.stringify(sendFilters),
          DisplayFilters: JSON.stringify(sendDisplayFilters),
          MTFullFilters: JSON.stringify(sendFiltersParams),
          Title: filterTitle,
          Type: type,
          DefaultFilter: defaultFilter,
        });
      } else {
        await deleteFavoriteFilter(isFavorite.id);
      }

      setFilterTitle("");
      setDefaultFilter(false);
      setReRenderFilterSettings(!reRenderFilterSettings);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  function openDialog() {
    setIsSaved(true);
  }

  function closeDialog() {
    setIsSaved(false);
    setFilterTitle("");
  }

  return (
    <>
      {filters && (
        <>
          {(loading && (
            <FlexCol className="flex w-6 h-6">
              <Loading />
            </FlexCol>
          )) || (
            <FavoriteButton
              isFavorite={isFavorite}
              onToggle={isFavorite.state && isFavorite.id ? toggleFavorite : openDialog}
            />
          )}
          <DialogComponent open={isSaved} toggleDialog={closeDialog}>
            <FlexCol className="w-full tablet:w-[600px] shrink">
              <FlexRow className="mb-9">
                <h3 className="text-black">Назовите набор фильтров</h3>
              </FlexRow>
              <FlexRow className="mb-9">
                <InputFilterSet initial={filterTitle} onChange={setFilterTitle} />
              </FlexRow>
              <FlexRow className="mb-[18px] tablet:mb-9">
                <div className="flex gap-[8px]">
                  <button
                    onClick={() => setDefaultFilter(!defaultFilter)}
                    className="hover:cursor-pointer"
                  >
                    <CustomRadio checked={defaultFilter} />
                  </button>
                  <span className="text-[#111928] text-[14px] tablet:text-[16px]">
                    Установить по умолчанию
                  </span>
                </div>
              </FlexRow>
              <FlexRow className="flex-wrap tablet:flex-nowrap justify-between tablet:px-10 pt-[24px] tablet:pt-10 tablet:-mx-10 border-t gap-[10px] tablet:gap-0">
                <FlexCol className={"w-full tablet:w-fit order-2 tablet:order-1"}>
                  <TGButton onClick={closeDialog}>Отмена</TGButton>
                </FlexCol>
                <FlexCol className={"w-full tablet:w-fit order-1 tablet:order-2"}>
                  <TGButton color="bluedark" icon={<SaveIcon />} onClick={toggleFavorite}>
                    Сохранить
                  </TGButton>
                </FlexCol>
              </FlexRow>
            </FlexCol>
          </DialogComponent>
        </>
      )}
    </>
  );
};
